/**
 * Shared localizer for system-message text. System messages (recording /
 * call-end / transcript / group-membership) are generated in ENGLISH and
 * stored in the DB; every surface that shows them (chat bubbles, chat-list
 * previews, topics overview, OS notifications) localises at render time by
 * matching the known templates below. Unknown text falls through unchanged.
 */
import { SYSTEM_MESSAGE_PREFIX } from "lib/emitter";

export type TranslateFn = (key: string, vars?: Record<string, string | number>) => string;

// Single source of truth for the marker lives in lib/emitter (acyclic: emitter
// imports only mitt). A drifting local copy here would silently split the
// notifiability predicate from the render-time strippers.
const SYSTEM_PREFIX = SYSTEM_MESSAGE_PREFIX;

/**
 * System messages the user must still be NOTIFIED about, mirroring the backend
 * predicates in MessageService (isCallAnnouncementSystemMessage /
 * isArtifactSystemMessage): the group call announcement — the only ring channel
 * for group conferences — and post-call artifact links (recording, transcript,
 * meeting insights), which arrive minutes after the other participant left.
 * Everything else (call ended, recording started/stopped, membership changes)
 * stays silent.
 */
export function isNotifiableSystemMessage(text: string | null | undefined): boolean {
  if (!text || !text.startsWith(SYSTEM_PREFIX)) return false;
  const body = text.substring(SYSTEM_PREFIX.length);
  return (
    body.startsWith("Video call started") ||
    body.startsWith("Recording available") ||
    body.startsWith("Transcript ready") ||
    body.startsWith("Meeting insights ready")
  );
}

export function localizeSystemTextFragment(text: string, tFn: TranslateFn): string {
  if (!text) return text;
  const trimmed = text.trim();
  if (!trimmed) return text;

  // Exact-match labels
  const exact: Record<string, string> = {
    "Recording started": "Recording started",
    "Recording available:": "Recording available:",
    "Recording stopped": "Recording stopped",
    "Recording": "Recording",
    "A new member joined the group": "A new member joined the group",
  };
  if (Object.prototype.hasOwnProperty.call(exact, trimmed)) {
    return text.replace(trimmed, () => tFn(exact[trimmed]));
  }

  // "Transcript ready (N segments):"
  const tsMatch = trimmed.match(/^Transcript ready \((\d+)\s*segments?\):?$/i);
  if (tsMatch) {
    const count = Number(tsMatch[1]);
    return text.replace(trimmed, () => tFn("Transcript ready ({{count}} segments):", { count }));
  }

  // "Meeting insights ready" with optional "(N topics)" / "(N action items)" /
  // "(N topics, M action items)" suffix. The string is generated client-side
  // in ConferenceView when post-recording analysis finishes — we localise here
  // at render time so it works for messages already stored in the DB.
  const miMatch = trimmed.match(/^Meeting insights ready(?:\s*\(([^)]+)\))?$/i);
  if (miMatch) {
    const detail = miMatch[1];
    if (!detail) return text.replace(trimmed, () => tFn("Meeting insights ready"));
    // Detail like "2 topics" or "3 action items" or "2 topics, 3 action items".
    const parts: string[] = [];
    const topicsMatch = detail.match(/(\d+)\s*topics?/i);
    if (topicsMatch) {
      parts.push(tFn("{{count}} topics", { count: Number(topicsMatch[1]) }));
    }
    const actionsMatch = detail.match(/(\d+)\s*action\s*items?/i);
    if (actionsMatch) {
      parts.push(tFn("{{count}} action items", { count: Number(actionsMatch[1]) }));
    }
    const joined = parts.length ? ` (${parts.join(", ")})` : "";
    return text.replace(trimmed, () => tFn("Meeting insights ready") + joined);
  }

  // "Video call ended (duration: 48s)" / "(duration: 2m 15s)" / "(duration: 1h 5m)"
  const callEnd = trimmed.match(/^Video call ended\s*\(duration:\s*([^)]+)\)$/i);
  if (callEnd) {
    const duration = callEnd[1];
    return text.replace(trimmed, () => tFn("Video call ended (duration: {{duration}})", { duration }));
  }
  // Bare "Video call ended" or "Call ended"
  if (/^Video call ended$/i.test(trimmed)) return text.replace(trimmed, () => tFn("Video call ended"));
  if (/^Call ended$/i.test(trimmed)) return text.replace(trimmed, () => tFn("Call ended"));

  // "Missed call" / "Call cancelled" / "Call declined"
  if (/^Missed call$/i.test(trimmed)) return text.replace(trimmed, () => tFn("Missed call"));
  if (/^Call cancelled$/i.test(trimmed)) return text.replace(trimmed, () => tFn("Cancelled call"));
  if (/^Call declined$/i.test(trimmed)) return text.replace(trimmed, () => tFn("Call declined"));

  // Group membership templates (generated client-side in English, see
  // GroupInfoPanel.sendSystemMessage / MessageThreadItem / PageJoinGroup).
  const addedMatch = trimmed.match(/^(.+) added (.+) to the group$/i);
  if (addedMatch) {
    return text.replace(trimmed, () =>
      tFn("{{actor}} added {{target}} to the group", { actor: addedMatch[1], target: addedMatch[2] })
    );
  }
  const removedMatch = trimmed.match(/^(.+) was removed from the group$/i);
  if (removedMatch) {
    return text.replace(trimmed, () => tFn("{{target}} was removed from the group", { target: removedMatch[1] }));
  }
  const leftMatch = trimmed.match(/^(.+) left the group$/i);
  if (leftMatch) {
    return text.replace(trimmed, () => tFn("{{actor}} left the group", { actor: leftMatch[1] }));
  }
  const joinedMatch = trimmed.match(/^(.+) joined the group$/i);
  if (joinedMatch) {
    return text.replace(trimmed, () => tFn("{{actor}} joined the group", { actor: joinedMatch[1] }));
  }

  return text;
}
